/*
  Cloudinary already resizes, re-encodes and serves these images from its CDN,
  and the `srcSet` below picks the width. Routing them through `next/image`
  would put a second optimiser in front of that for no gain, so the plain
  element is deliberate.
*/
/* eslint-disable @next/next/no-img-element */
import { fill, pad } from '@cloudinary/url-gen/actions/resize';
import { Background } from '@cloudinary/url-gen/qualifiers/background';
import { Color } from '@cloudinary/url-gen/qualifiers/color';
import { CheckCircleIcon } from 'components/icons';
import { Link } from 'components/link';
import { ListingTypePill } from 'components/listing-type-pill/listing-type-pill';
import ShareListingDialog from 'components/share-listing-dialog/share-listing-dialog';
import {
  Button,
  EllipsisText,
  Flex,
  Icon,
  ListCard,
  Tag,
  Text,
} from 'components/ui';
import { format, formatDistanceToNow } from 'date-fns';
import { useDialogToggle } from 'hooks/use-dialog-toggle';
import {
  ListingMedia,
  ListingRead,
  ListingTypeEnum,
  VisibilityEnum,
} from 'lib/api/generated';
import {
  CARD_IMAGE_SIZES,
  THUMBNAIL_IMAGE_SIZES,
  THUMBNAIL_IMAGE_WIDTHS,
  buildResponsiveImage,
} from 'lib/cloudinary-image';
import {
  getListingStatusLabel,
  getLivestockTypeIconName,
  getMediaAltText,
} from 'lib/helpers';
import { buildPublicListingPath } from 'lib/listing-utils';
import Image from 'next/legacy/image';
import { useRouter } from 'next/router';
import { rgba } from 'polished';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BiCalendar } from 'react-icons/bi';
import { DATE_FORMAT, cld } from 'settings/config';
import styled from 'styled-components';
import { breakpoints } from 'styles/breakpoints';
import { globalTheme } from 'styles/global-theme';
import CattleSalePlaceholder from '../images/sale_Cattle.png';
import GoatSalePlaceholder from '../images/sale_Goat.png';
import SheepSalePlaceholder from '../images/sale_Sheep.png';
import CattleWantedPlaceholder from '../images/wanted_Cattle.png';
import GoatWantedPlaceholder from '../images/wanted_Goat.png';
import SheepWantedPlaceholder from '../images/wanted_Sheep.png';

const Card = styled(ListCard)<{ $isFilled?: boolean; $isUnderOffer?: boolean }>`
  width: 100%;
  height: 100%;
  padding: 0;
  display: flex;
  flex-direction: column;
  background-color: ${(props) =>
    !props.$isFilled
      ? globalTheme.colors.white
      : props.$isUnderOffer
      ? rgba(globalTheme.colors.lightBlue, 0.2)
      : rgba(globalTheme.colors.lightRed, 0.2)};
  box-shadow: 0px 10px 15px -3px rgba(0, 0, 0, 0.1);
  border-radius: ${globalTheme.space[0]};
  border: 1px solid
    ${(props) =>
      props.$isUnderOffer
        ? globalTheme.colors.blue
        : props.$isFilled
        ? globalTheme.colors.pink
        : '#d9d9d9'};
  overflow: hidden;
  position: relative;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-color: ${rgba(globalTheme.colors.darkBlue, 0.8)};
  z-index: 9;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  justify-content: space-between;
  margin: 0 ${globalTheme.space[0]};
  flex: 1;
`;

const InlineText = styled.div`
  display: flex;
  gap: 14px;
  align-items: center;
`;

const SecondaryText = styled(EllipsisText).attrs({
  fontSize: 2,
  lineHeight: 'heading',
})`
  width: fit-content;
`;

const TopMediaArea = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 3/2;
  overflow: hidden;
  background-color: ${globalTheme.colors.lightGray};

  @media screen and (max-width: ${breakpoints.small}) {
    width: 100%;
  }
`;

const MediaBackground = styled.div<{ isContain?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 76px;
  background-color: ${globalTheme.colors.white};

  img {
    width: 100% !important;
    height: 100% !important;
    object-fit: ${({ isContain }) =>
      isContain ? 'contain' : 'cover'} !important;
    object-position: center !important;
  }

  > span {
    height: 100% !important;
  }
`;

const LivestockTag = styled(Tag)<{ privateTag?: boolean }>`
  padding: ${globalTheme.space[1]} 14px;
  color: ${(props) =>
    props.privateTag ? globalTheme.colors.white : globalTheme.colors.blue};
  * {
    fill: ${(props) =>
      props.privateTag ? globalTheme.colors.white : globalTheme.colors.blue};
  }
`;

const PillWrapper = styled(Flex)`
  position: absolute;
  top: 0;
  left: 0;
  padding: 14px;
  display: flex;
  width: 100%;
  gap: ${globalTheme.space[1]};
`;

const VerifiedAgentTag = styled(Tag)`
  padding: ${globalTheme.space[1]} 14px;
  color: ${globalTheme.colors.blue};
  background-color: ${globalTheme.colors.lightBlue};
  * {
    fill: ${globalTheme.colors.blue};
  }
`;

const ImageWrapper = styled.div`
  width: 80px;
  height: max-content;
  display: flex;
  justify-content: center;
  top: 10px;
  overflow: hidden;
  border-radius: 4px;

  img {
    border-radius: 4px;
    /*
      The browser may pick a 160 or 240px candidate from the srcSet, so the
      element has to be sized in CSS or it lays out at that intrinsic pixel
      width and overflows this 80px box.
    */
    width: 100%;
    height: auto;
  }
`;

const AddressWrapper = styled.div`
  display: flex;
  min-width: fit-content;
  word-break: break-word;
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

// Real anchor wrapping the listing title: gives crawlers (and JS-less AI bots)
// a followable link with keyword-rich anchor text. The `::after` stretches over
// the whole `Card` (which is `position: relative`) so the entire card is one
// real link — clicking anywhere navigates, and cmd/ctrl/shift/middle-click open
// the listing in a new tab/window natively (via next/link). `position: static`
// keeps the `::after` anchored to the Card, not the link itself.
const TitleLink = styled.a`
  display: block;
  min-width: 0;
  color: inherit;
  text-decoration: none;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 1;
  }
`;

const CalenderIcon = styled(BiCalendar)`
  width: 24px;
  height: 24px;
`;

const HeadPriceContainer = styled(Flex)<{ isBuyListing?: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 76px;
  padding: 0 ${globalTheme.space[1]};
  background-color: ${(props) =>
    props.isBuyListing ? globalTheme.colors.pink : globalTheme.colors.darkBlue};
  color: ${globalTheme.colors.white};
`;

interface ListingsCardProps {
  data: ListingRead;
  route: string;
  setMinWidth?: boolean;
  isLoading?: boolean;
  refetch?: any;
  isFeaturedListing?: boolean;
}

export const getPlaceholderImage = (
  product: string,
  type?: ListingTypeEnum,
  layout?: 'fill' | 'fixed' | 'intrinsic' | 'responsive'
) => {
  // Alt text for the species fallback art, e.g. "Agora cattle for sale fallback
  // image" (or "...wanted..." for Buy listings, which use a different image).
  const species: { [key: string]: string } = {
    CT: 'cattle',
    SH: 'sheep',
    GT: 'goats',
  };
  const saleOrWanted = type === ListingTypeEnum.Sell ? 'for sale' : 'wanted';
  const placeholderAlt = species[product]
    ? `Agora ${species[product]} ${saleOrWanted} listing image`
    : 'Agora livestock listing image';

  switch (product) {
    case 'CT':
      return (
        <Image
          src={
            type === ListingTypeEnum.Sell
              ? CattleSalePlaceholder
              : CattleWantedPlaceholder
          }
          layout={layout}
          objectFit={'cover'}
          alt={placeholderAlt}
        />
      );
    case 'SH':
      return (
        <Image
          src={
            type === ListingTypeEnum.Sell
              ? SheepSalePlaceholder
              : SheepWantedPlaceholder
          }
          layout={layout}
          objectFit={'cover'}
          alt={placeholderAlt}
        />
      );
    case 'GT':
      return (
        <Image
          src={
            type === ListingTypeEnum.Sell
              ? GoatSalePlaceholder
              : GoatWantedPlaceholder
          }
          layout={layout}
          objectFit={'cover'}
          alt={placeholderAlt}
        />
      );
  }
};

const ActionButton = styled(Button)<{ isBuyListing?: boolean }>`
  border-radius: 4px;
  font-size: 1.114rem;
  flex: 1;
  background-color: ${(props) =>
    props.isBuyListing
      ? globalTheme.colors.pink
      : globalTheme.elements.navbar.backgroundColor};
`;
const ShareButton = styled.div`
  border-radius: 4px;
  font-size: 1.114rem;
  padding: 4px 8px;
  background-color: ${globalTheme.elements.navbar.hoverColor};
  width: max-content;
  color: ${globalTheme.colors.white};
  display: flex;
  align-items: center;
`;

const ActionButtonContainer = styled(Flex)`
  gap: ${globalTheme.space[0]};
  margin: ${globalTheme.space[0]} 0;
  // Sit above the stretched title-link overlay so Share / View stay clickable.
  position: relative;
  z-index: 2;
`;

// Public marketplace listings link to the slugged detail route
// (`/marketplace/listing/<id>/<slug>`); app routes have no slug route, so they
// keep the bare-id path.
const buildListingHref = (listing: ListingRead, route: string): string =>
  route.includes('app')
    ? `${route}/listing/${listing.id}`
    : buildPublicListingPath(listing);

function PublicListingsCard({
  data,
  route,
  isFeaturedListing = false,
}: ListingsCardProps) {
  const router = useRouter();
  const [shareUrl, setShareUrl] = useState<string>('');
  const [listingId, setListingId] = useState<string>('');
  const [isCardClicked, setIsCardClicked] = useState<boolean>(false);

  const [
    shareListingDialogVisible,
    openShareListingDialog,
    closeShareListingDialog,
  ] = useDialogToggle(false);

  const {
    type,
    title,
    listing_address,
    media,
    product,
    quantity_label,
    prices_data,
    form_data,
    last_updated,
    under_offer,
    sold,
    sold_price,
    sold_price_type,
    sold_hide_price,
    org,
    visibility,
    quantity_data,
    date_from,
    date_to,
    price_method,
  } = data;

  const isAuction = price_method === 'AUCTION';

  useEffect(() => {
    if (listingId) {
      setShareUrl(
        window.location.origin +
          buildPublicListingPath({
            id: listingId,
            title,
            metadata: data.metadata,
          })
      );
    }
  }, [listingId, title, data.metadata]);

  const handleListingView = async (data: ListingRead, route: string) => {
    setIsCardClicked(true);
    await router.push(buildListingHref(data, route));
    isFeaturedListing && router.reload();
    setIsCardClicked(false);
  };

  const images = media.filter(
    (media) =>
      (media.resource_type === 'image' || media.resource_type === 'video') &&
      media.format !== 'pdf'
  );

  // Alt-text fallback for the listing cover image, mirroring the page-title
  // pattern: "<title>, <suburb> <state> - Agora Livestock listing <id>".
  // Suburb/state aren't always present, so the location segment is dropped when
  // both are absent. A media item's own description still takes precedence.
  const altLocation = [listing_address?.suburb, listing_address?.state]
    .filter(Boolean)
    .join(' ');
  const imageAltText = `${[title, altLocation]
    .filter(Boolean)
    .join(', ')} - Agora Livestock listing ${data.id}`;

  const coverMedia = images[0] as ListingMedia | undefined;
  const logoMedia = org?.logo as ListingMedia | undefined;

  // The URL sets are memoised on the media they describe. `data` is stable
  // between re-renders, so a filter or refetch tick re-renders the card without
  // touching `src`/`srcSet`, and the browser has no reason to fetch again.
  const coverImage = useMemo(
    () =>
      coverMedia
        ? buildResponsiveImage((width) => {
            const crop = fill().aspectRatio('3:2').width(width);
            return coverMedia.resource_type === 'image'
              ? cld
                  .image(coverMedia.public_id)
                  .format('jpg')
                  .quality('auto')
                  .resize(crop)
              : cld.video(coverMedia.public_id).format('jpg').resize(crop);
          })
        : null,
    [coverMedia]
  );

  // Shown in place of the cover image when a listing has no media of its own.
  const logoImage = useMemo(
    () =>
      logoMedia
        ? buildResponsiveImage((width) =>
            cld
              .image(logoMedia.public_id)
              .format('jpg')
              .quality('auto')
              .resize(
                pad()
                  .aspectRatio('3:2')
                  .width(width)
                  .background(Background.color(Color.WHITE))
              )
          )
        : null,
    [logoMedia]
  );

  // The small seller logo pinned over the cover image.
  const thumbnailImage = useMemo(
    () =>
      logoMedia
        ? buildResponsiveImage(
            (width) =>
              cld
                .image(logoMedia.public_id)
                .format('jpg')
                .quality('auto')
                .resize(fill().width(width)),
            THUMBNAIL_IMAGE_WIDTHS
          )
        : null,
    [logoMedia]
  );

  const handleShareClick = (e: any, id: string) => {
    setListingId(id);
    e.stopPropagation();
    openShareListingDialog();
  };

  const getBreedType = () => {
    if (form_data?.breed === 'Purebred') {
      return form_data?.purebred_breed
        ? `${form_data?.purebred_breed} (purebred)`
        : '';
    } else {
      return form_data?.mixed_breed ? form_data?.mixed_breed : '';
    }
  };

  function truncateTotalCharsTo20(text: string) {
    if (text.length > 30) {
      return text.substring(0, 30) + '...';
    }
    return text;
  }

  const getAddress: string = useMemo(() => {
    const suburb = listing_address?.suburb ? listing_address.suburb : '';
    const state = listing_address?.state ? listing_address.state : '';
    const postcode = listing_address?.postcode ? listing_address.postcode : '';
    const concatenatedAddress = `${suburb}${
      suburb && state ? ', ' : ''
    }${state}${state && postcode ? ' ' : ''}${postcode}`;

    const truncatedAddress = truncateTotalCharsTo20(concatenatedAddress);
    return truncatedAddress ?? '-';
  }, [
    listing_address?.suburb,
    listing_address?.state,
    listing_address?.postcode,
  ]);

  const getPrice = useCallback(
    (price_data, sold, soldPrice, soldPriceType, hideSoldPrice) => {
      const soldLabelMap: any = {
        PER_HEAD: '/head',
        LIVEWEIGHT: '/kg LW',
        HSCW: '/kg HSCW',
      };

      // An auction keeps showing "Auction <date>" after it closes (it never has a sold price).
      if (price_data && price_data?.auction) {
        return {
          price: 'Auction',
          label: date_from
            ? format(new Date(date_from), DATE_FORMAT.listing)
            : '',
        };
      }
      if (sold) {
        if (hideSoldPrice) {
          return { price: '', label: '' };
        }

        return {
          price: soldPrice ? `$${soldPrice}` : '',
          label: soldLabelMap[soldPriceType] || '',
        };
      }
      if (price_data && price_data?.poa) {
        return { price: 'POA', label: '' };
      }
      if (
        !price_data ||
        !price_data.prices ||
        !Array.isArray(price_data.prices) ||
        price_data.prices.length === 0
      ) {
        return { price: '', label: '' };
      }

      const prices = price_data.prices;

      const priceValues = prices.map((item: any) => item.price);
      const units = prices.map((item: any) => item.unit);

      if (prices.length === 1) {
        return {
          price: priceValues[0],
          label: `${units[0]}`,
        };
      } else {
        return {
          price: priceValues.join(', '),
          label: `${units.join(', ')}`,
        };
      }
    },
    [date_from]
  );

  return (
    <Card $isFilled={!!sold} $isUnderOffer={!!under_offer}>
      {isCardClicked && (
        <LoadingOverlay>
          <Tag fontSize={2} padding={2} height={4}>
            Loading...
          </Tag>
        </LoadingOverlay>
      )}

      <TopMediaArea>
        <MediaBackground
          isContain={!(images && images.length > 0) && !!org?.logo}
        >
          {coverImage && coverMedia ? (
            <img
              {...coverImage}
              sizes={CARD_IMAGE_SIZES}
              alt={getMediaAltText(coverMedia, imageAltText)}
            />
          ) : logoImage ? (
            <img
              {...logoImage}
              sizes={CARD_IMAGE_SIZES}
              alt={`${org?.name || 'Seller'} logo`}
            />
          ) : (
            product && getPlaceholderImage(product, type, 'fill')
          )}
        </MediaBackground>

        <PillWrapper wrap='wrap' gap={0} justifyContent='space-between'>
          <Flex gap={0} maxWidth={'max-content'}>
            <ListingTypePill type={type} />
            {visibility === VisibilityEnum.Private && (
              <Flex gap={0} wrap='wrap' maxWidth={'max-content'}>
                <LivestockTag backgroundColor='red' privateTag>
                  <SecondaryText>Private</SecondaryText>
                </LivestockTag>
              </Flex>
            )}
          </Flex>
          {thumbnailImage && images.length > 0 && (
            <ImageWrapper>
              <img
                {...thumbnailImage}
                sizes={THUMBNAIL_IMAGE_SIZES}
                alt={`${org?.name || 'Seller'} logo`}
              />
            </ImageWrapper>
          )}
          {!org?.logo?.public_id && org?.verified_agent && (
            <VerifiedAgentTag>
              <CheckCircleIcon
                width={18}
                height={18}
                color={globalTheme.colors.blue}
              />
              <SecondaryText>Agent</SecondaryText>
            </VerifiedAgentTag>
          )}
        </PillWrapper>

        <HeadPriceContainer
          isBuyListing={type === ListingTypeEnum.Buy}
          alignItems='center'
          justifyContent='space-between'
        >
          <Flex
            alignItems='center'
            justifyItems='center'
            justifyContent='space-between'
            gap={0}
          >
            <Flex direction='column' maxWidth={'max-content'}>
              <Text
                lineHeight='heading'
                fontSize={6}
                maxWidth={'max-content'}
                fontWeight='bold'
                color='white'
              >
                {quantity_data?.head || ''}
              </Text>
              <Text
                color='white'
                lineHeight='heading'
                fontSize={3}
                maxWidth={'max-content'}
              >
                {quantity_data?.unit || ''}
              </Text>
            </Flex>
            <Flex direction='column'>
              <Text
                fontSize={6}
                textAlign='end'
                fontWeight='bold'
                lineHeight='heading'
                color={sold && !isAuction ? 'red' : undefined}
              >
                {!sold && under_offer ? (
                  // LPD-5654 - match the "Auction" label's font style. Text never
                  // inherits sizing (it always emits a fontSize), so set it explicitly.
                  // Buy listings keep white: their banner is already pink.
                  <Text
                    as='span'
                    fontSize={6}
                    fontWeight='bold'
                    lineHeight='heading'
                    color={type === ListingTypeEnum.Buy ? 'white' : 'pink'}
                  >
                    Under Offer
                  </Text>
                ) : null}{' '}
                {
                  getPrice(
                    prices_data,
                    sold || under_offer,
                    sold_price,
                    sold_price_type,
                    sold_hide_price
                  )?.price
                }
              </Text>
              <Text
                lineHeight='heading'
                textAlign='end'
                fontSize={3}
                color={sold && !isAuction ? 'red' : undefined}
              >
                {
                  getPrice(
                    prices_data,
                    sold || under_offer,
                    sold_price,
                    sold_price_type,
                    sold_hide_price
                  )?.label
                }
              </Text>
            </Flex>
          </Flex>
        </HeadPriceContainer>
      </TopMediaArea>
      <Content>
        <TitleWrapper>
          <Link href={buildListingHref(data, route)} passHref>
            <TitleLink
              onClick={(e) => {
                // Only show the loading overlay for a plain navigation. Modifier
                // clicks open a new tab, so don't leave this tab stuck loading.
                if (!e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
                  setIsCardClicked(true);
                }
              }}
            >
              <EllipsisText fontWeight='bold' lineHeight='heading' fontSize={4}>
                {title}
              </EllipsisText>
            </TitleLink>
          </Link>
        </TitleWrapper>
        <div>
          <InlineText>
            <Icon component='MapPinIcon' size={2} />
            <AddressWrapper>
              <SecondaryText>{getAddress}</SecondaryText>
            </AddressWrapper>
          </InlineText>
          <InlineText>
            {product && (
              <Icon component={getLivestockTypeIconName(product)} size={2} />
            )}
            <SecondaryText>
              {form_data?.breed && getBreedType() ? `${getBreedType()}` : '-'}
            </SecondaryText>
          </InlineText>

          {sold && (
            <InlineText>
              <CalenderIcon color='red' />
              <SecondaryText color='red'>
                {isAuction ? 'Auctioned' : getListingStatusLabel(type)} on{' '}
                {format(new Date(sold), DATE_FORMAT.listing)}
              </SecondaryText>
            </InlineText>
          )}
          {under_offer && !sold && (
            <InlineText>
              <CalenderIcon color='blue' />
              <SecondaryText color='blue'>
                Under Offer (
                {format(new Date(under_offer), DATE_FORMAT.listing)})
              </SecondaryText>
            </InlineText>
          )}

          {!sold && !under_offer && (
            <InlineText>
              <CalenderIcon />
              <SecondaryText>
                {date_from || date_to
                  ? `Updated
                  ${formatDistanceToNow(new Date(last_updated as string), {
                    addSuffix: true,
                  })}`
                  : '-'}{' '}
              </SecondaryText>
            </InlineText>
          )}

          {/* Commented on redesigning of card 27 May */}
          {/* {networks && !!networks.length && (
          <Tooltip label={listingNetworks}>
            <InlineGridWrap>
              <Icon component='Marketplace' size={2} />
              <OneLineText>{listingNetworks}</OneLineText>
            </InlineGridWrap>
          </Tooltip>
        )} */}
        </div>
        <ActionButtonContainer gap={0}>
          <ShareButton onClick={(e) => handleShareClick(e, data.id)}>
            Share
          </ShareButton>
          <ActionButton
            size='small'
            isBuyListing={type === ListingTypeEnum.Buy}
            onClick={(e: any) => {
              handleListingView(data, route);
            }}
          >
            View {type === ListingTypeEnum.Buy ? 'Wanted' : 'Sell'} Listing
            <Icon component='ChevronNextIcon' size={1} />
          </ActionButton>
        </ActionButtonContainer>
        <ShareListingDialog
          isOpen={shareListingDialogVisible}
          onDismiss={closeShareListingDialog}
          shareUrl={shareUrl}
          listingDetails={data}
        />
      </Content>
    </Card>
  );
}

export default PublicListingsCard;
